'use client'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaUnlock, FaLock, FaTimes, FaLongArrowAltRight } from 'react-icons/fa'
import Modal from './Modal'
import { TbFaceId } from 'react-icons/tb'
import { useAuth } from '../contexts/AuthContext'
import * as accountsApi from '../lib/api/accounts.ts'

const AgeGateBanner = () => {
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(true)
  const [isStartingVerification, setIsStartingVerification] = useState(false)
  const { ageGateModalOpen, setAgeGateModalOpen, user } = useAuth()

  const handleClose = () => {
    setIsVisible(false)
  }

  const startVerification = async () => {
    if (!user || isStartingVerification) return

    try {
      setIsStartingVerification(true)

      const idToken = await user.getIdToken()

      const response = await accountsApi.startAgeCheck(idToken)

      const data = await response.json()

      if (!response.ok) {
        throw new Error(
          data?.message || data?.error || 'Failed to start age verification',
        )
      }

      if (data?.verificationUrl && typeof window !== 'undefined') {
        window.location.href = data.verificationUrl
        return
      }

      throw new Error('Missing verification URL')
    } catch (error) {
      console.error('Error starting age verification:', error)
    } finally {
      setIsStartingVerification(false)
    }
  }

  if (!isVisible) return null

  return (
    <>
      {/* Taller than a one-line strip, with an icon and the modal's privacy
          line: as a single sentence on the header's grey it was easy to read
          past (2026-10-14). The full why stays behind Learn more. */}
      <div className="relative bg-[--secondary-bg-color] px-3 py-3 pr-9 text-white shadow-md md:px-8 md:py-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-6">
          <div className="flex min-w-0 flex-1 items-start gap-3 md:items-center md:gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 md:h-12 md:w-12">
              <TbFaceId className="text-2xl text-indigo-300 md:text-[28px]" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-snug md:text-base">
                {t('ageGateBannerText')}
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-[11px] text-gray-400 md:text-xs">
                <FaLock className="shrink-0 text-[10px]" />
                {t('ageGateNoIdStored')}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-3 pl-[52px] md:pl-0">
            <button
              onClick={startVerification}
              disabled={isStartingVerification}
              className="inline-flex items-center justify-center rounded-full bg-[--main-bg-color] px-4 py-2 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-70 md:px-5 md:py-2.5"
            >
              <FaUnlock className="mr-2 text-xs" />
              {isStartingVerification ? t('starting') : t('verifyAge')}
            </button>

            <button
              onClick={() => setAgeGateModalOpen(true)}
              className="inline-flex items-center justify-center whitespace-nowrap text-xs font-medium text-gray-300 hover:text-white"
            >
              <span>{t('learnMore')}</span>
              <FaLongArrowAltRight className="ml-1 text-xs" />
            </button>
          </div>
        </div>

        <button
          onClick={handleClose}
          className="absolute right-2 top-2 text-xs text-white/80 hover:text-white"
          aria-label={t('close')}
        >
          <FaTimes />
        </button>
      </div>

      <Modal
        isOpen={ageGateModalOpen}
        closeModal={() => setAgeGateModalOpen(false)}
        title=""
        content={
          <>
            <div className="mx-auto w-full max-w-[360px] overflow-hidden rounded-[24px] text-white shadow-2xl">
              <div className="px-6 py-6 text-center">
                <h2 className="text-xl font-semibold mb-2">
                  {t('verifyYourAge')}
                </h2>

                <p className="text-sm text-gray-400 mb-8">
                  {t('ageGateModalBody')}
                </p>

                <div className="flex items-center justify-center gap-4 text-left mb-10">
                  <div className="flex flex-col items-center">
                    <div className="h-6 w-[3px] bg-indigo-500 rounded-full" />
                    <div className="h-12 w-[3px] bg-gray-300 rounded-full" />
                  </div>

                  <div className="space-y-4">
                    <div className="text-sm font-medium flex items-center justify-center gap-2">
                      <TbFaceId /> {t('quickAgeCheck')}
                    </div>

                    <div className="text-sm font-medium text-gray-400 flex items-center justify-center gap-2">
                      <FaUnlock /> {t('unlockEverything')}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-white font-medium">
                  {t('ageGateNoIdStored')}
                </p>

                <p className="text-xs text-gray-400">
                  {t('ageGateDiditNotice')}{' '}
                  <a
                    href="https://didit.me/terms/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-300"
                  >
                    {t('privacyNotice')}
                  </a>
                  .
                </p>
              </div>
            </div>
          </>
        }
        confirmation={
          <p className="flex items-center justify-center gap-2">
            <FaUnlock />
            {isStartingVerification ? t('starting') : t('startVerification')}
          </p>
        }
        onConfirm={async () => {
          setAgeGateModalOpen(false)
          await startVerification()
        }}
      />
    </>
  )
}

export default AgeGateBanner
